import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '../../types';
import type { FormConfig, Page } from '../../config/types';
import { approveForm, updateForm } from '../../service/form';
import { ApiError } from '../../utils/api-error';
import type { FetchFormResponse } from '../../service/types';
import { extractConditionalQuestions } from '../../utils/config-utils';
import { getQuarterCompletion } from './quarter-completion';
import ScsQuarterCompletionModal from './ScsQuarterCompletionModal';
import ScsManagerDetailsReminder from './ScsManagerDetailsReminder';

type Props = {
  enabled: boolean;
  formId: string;
  config: FormConfig;
  subsection: string;
  getToken: () => Promise<string>;
};

export function useScsQuarterCompletion({ enabled, formId, config, subsection, getToken }: Props) {
  const queryClient = useQueryClient();
  const [showManagerReminder, setShowManagerReminder] = useState(false);
  const [pending, setPending] = useState<{
    completion: NonNullable<ReturnType<typeof getQuarterCompletion>>;
    data: FetchFormResponse;
    page: Page;
    managerEmail: string;
  } | null>(null);
  const resolve = useRef<((path?: string) => void) | null>(null);
  const close = (path?: string) => {
    setShowManagerReminder(false);
    setPending(null);
    resolve.current?.(path);
    resolve.current = null;
  };
  const afterSave = async (data: FetchFormResponse | undefined, page: Page) => {
    if (!enabled || !data) return;
    const completion = getQuarterCompletion(config, data, subsection);
    if (!completion) return;
    return new Promise<string | undefined>((done) => {
      resolve.current = done;
      setPending({
        completion,
        data,
        page,
        managerEmail: String(data.answers?.['person-details-manager-email'] || '').trim(),
      });
    });
  };
  const onDecision = async (send: boolean, managerEmail?: string) => {
    if (!pending) return;
    const { data, page } = pending;
    const token = await getToken();
    if (send) {
      if (!managerEmail) throw new Error('Manager email is required');
      const savedManager = String(data.answers?.['person-details-manager-email'] || '')
        .trim()
        .toLowerCase();
      if (managerEmail !== savedManager) {
        await updateForm(
          token,
          {
            'subsection-id': 'cover-sheet',
            'page-id': 'person-details',
            'is-save-unfinished': false,
            'defer-scs-approval': true,
            answers: { 'person-details-manager-email': managerEmail },
          },
          { form_id: formId },
        );
        setPending({
          ...pending,
          data: {
            ...data,
            answers: { ...data.answers, 'person-details-manager-email': managerEmail },
          },
        });
      }
      try {
        await approveForm(
          token,
          {
            action: 'submit',
            approverEmail: managerEmail,
            approvalQuarter: pending.completion.quarter,
            approvalYear: data.scsProgress?.year || String(data.answers?.['report-period'] || ''),
          },
          { form_id: formId },
        );
      } catch (failure) {
        const message =
          failure instanceof ApiError
            ? failure.message
            : 'Unable to send your form for approval. Please try again.';
        throw new ApiError(
          managerEmail !== pending.managerEmail.toLowerCase()
            ? `Manager Email has been updated. ${message}`
            : message,
        );
      }
      await queryClient.invalidateQueries({ queryKey: [Query.FormData] });
      if (managerEmail !== pending.managerEmail.toLowerCase()) setShowManagerReminder(true);
      else close('/scs?my_forms=1&admin=0');
      return;
    } else {
      const questions = extractConditionalQuestions(('questions' in page && page.questions) || []);
      const answers = Object.fromEntries(
        questions
          .filter((question) => data.answers?.[question.name] !== undefined)
          .map((question) => [question.name, data.answers![question.name]]),
      );
      await updateForm(
        token,
        {
          'subsection-id': subsection,
          'page-id': page.name,
          'is-save-unfinished': true,
          'defer-scs-approval': true,
          answers: page.pageType === 'list' ? { [page.name]: [] } : answers,
        },
        { form_id: formId },
      );
    }
    close();
  };
  return {
    afterSave,
    modal:
      pending &&
      (showManagerReminder ? (
        <ScsManagerDetailsReminder formId={formId} onClose={close} />
      ) : (
        <ScsQuarterCompletionModal
          quarter={pending.completion.quarter}
          missing={pending.completion.missing}
          managerEmail={pending.managerEmail}
          onDecision={onDecision}
          onMissingClose={() => close()}
        />
      )),
  };
}
